"use client";

import { useI18n } from "@/lib/i18n/I18nProvider";

/**
 * KpiDashboardMock — реалистичный KPI-дашборд с браузерной рамкой
 * Показывает ревение платежную заявку с KPI-карточками и графиком
 */

export function KpiDashboardMock() {
  const { t, locale } = useI18n();
  const isEn = locale === "en";

  const KPIS = isEn
    ? [
        { label: "RevPAR", value: "$186", change: "+12.3%", positive: true },
        { label: "ADR", value: "$247", change: "+5.1%", positive: true },
        { label: "Occupancy", value: "75.2%", change: "−2.4pp", positive: false },
        { label: "GOPPAR", value: "$92", change: "+18.7%", positive: true },
      ]
    : [
        { label: "RevPAR", value: "₽8,450", change: "+12.3%", positive: true },
        { label: "ADR", value: "₽11,230", change: "+5.1%", positive: true },
        { label: "Occupancy", value: "75.2%", change: "−2.4pp", positive: false },
        { label: "GOPPAR", value: "₽3,120", change: "+18.7%", positive: true },
      ];

  const REVENUE_ROWS = isEn
    ? [
        { i18nKey: "landing.mocks.kpi.dayMon14apr", amount: "$52,000", pct: 85 },
        { i18nKey: "landing.mocks.kpi.dayTue15apr", amount: "$57,000", pct: 92 },
        { i18nKey: "landing.mocks.kpi.dayWed16apr", amount: "$49,000", pct: 80 },
        { i18nKey: "landing.mocks.kpi.dayThu17apr", amount: "$55,000", pct: 90 },
      ]
    : [
        { i18nKey: "landing.mocks.kpi.dayMon14apr", amount: "₽480,000", pct: 85 },
        { i18nKey: "landing.mocks.kpi.dayTue15apr", amount: "₽520,000", pct: 92 },
        { i18nKey: "landing.mocks.kpi.dayWed16apr", amount: "₽450,000", pct: 80 },
        { i18nKey: "landing.mocks.kpi.dayThu17apr", amount: "₽510,000", pct: 90 },
      ];

  return (
    <div className="w-full bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200">
      {/* Browser chrome */}
      <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <div className="ml-auto">
          <p className="text-xs text-slate-600 font-medium">app.keiront.com/kpi</p>
        </div>
      </div>

      {/* Dashboard content */}
      <div className="p-6 bg-white">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-900">KPI Dashboard</h2>
          <p className="text-xs text-slate-500 mt-1">
            {t("landing.mocks.kpi.period") || "Апрель 2026 · Отель Москва Центр"}
          </p>
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {KPIS.map((kpi) => (
            <div key={kpi.label} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <p className="text-xs text-slate-600 font-medium mb-2">{kpi.label}</p>
              <p className="font-mono text-sm font-bold text-slate-900 mb-2">{kpi.value}</p>
              <p
                className={`text-xs font-semibold ${
                  kpi.positive ? "text-keiro-green" : "text-amber-600"
                }`}
              >
                {kpi.change}
              </p>
              {/* Sparkline */}
              <svg viewBox="0 0 80 20" className="w-full h-4 mt-2">
                <polyline
                  fill="none"
                  stroke={kpi.positive ? "#1D9E75" : "#D97706"}
                  strokeWidth="1.5"
                  points={
                    kpi.positive
                      ? "0,15 10,12 20,10 30,8 40,5 50,7 60,4 70,3 80,2"
                      : "0,5 10,8 20,10 30,12 40,15 50,13 60,16 70,17 80,18"
                  }
                />
              </svg>
            </div>
          ))}
        </div>

        {/* Revenue by day table */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <p className="text-xs font-semibold text-slate-700">
              {t("landing.mocks.kpi.revenueByDaysTitle") || "Доход по дням (последние 7 дней)"}
            </p>
          </div>
          <div className="space-y-0">
            {REVENUE_ROWS.map((row, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 px-4 py-2.5 ${
                  i !== 3 ? "border-b border-slate-100" : ""
                }`}
              >
                <span className="text-xs text-slate-600 w-20 truncate">
                  {t(row.i18nKey)}
                </span>
                <span className="text-xs font-mono font-semibold text-slate-900 w-24">
                  {row.amount}
                </span>
                <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-keiro-green"
                    style={{ width: `${row.pct}%` }}
                  />
                </div>
                <span className="text-xs text-slate-500 w-12 text-right">{row.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Demo label */}
      <div className="bg-slate-50 border-t border-slate-200 px-6 py-2 text-center">
        <p className="text-xs text-slate-500">
          {t("landing.mocks.kpi.demoLabel") || "Демо-режим · реальный UI продукта"}
        </p>
      </div>
    </div>
  );
}
